import * as React from "react";
import { useSelector } from "react-redux";
import {
  DashboardMenuItem,
  MenuItemLink,
  useResourceDefinitions,
} from "react-admin";

import DashboardIcon from "@mui/icons-material/Home";
import CategoryIcon from "@mui/icons-material/Category";
import ComposeIcon from "@mui/icons-material/Schema";
import DeveloperIcon from "@mui/icons-material/DeveloperMode";
import CatalogIcon from "@mui/icons-material/LibraryBooks";
import ConfigIcon from "@mui/icons-material/Settings";
import AnalyticsIcon from "@mui/icons-material/Analytics";
import AssessmentIcon from "@mui/icons-material/Assessment";
import CableIcon from "@mui/icons-material/Cable";
import SecurityIcon from "@mui/icons-material/Security";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import VisibilityIcon from "@mui/icons-material/Visibility";
import AppsIcon from "@mui/icons-material/Apps";
import { useLocation, useNavigate } from "react-router";
import { useEffect, useState } from "react";
import AppExtensionProviderRegistry, {
  AppExtension,
} from "../../framework/app/app.extension.provider";
import {
  Collapse,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Paper,
} from "@mui/material";
import { useDynamicEntities } from "../../hooks/useDynamicEntities";
import { useDataModelHierarchy } from "../../hooks/useDataModelHierarchy";
import DataIcon from "@mui/icons-material/Storage";
import GeneratedIcon from "@mui/icons-material/AutoFixHigh";
import FrameworkIcon from "@mui/icons-material/Architecture";
import DomainIcon from "@mui/icons-material/Business";
import PeopleIcon from "@mui/icons-material/People";
import CommerceIcon from "@mui/icons-material/ShoppingCart";
import OperationsIcon from "@mui/icons-material/Settings";
import SchemaIcon from "@mui/icons-material/Schema";

const Menu = ({ onMenuClick, logout }) => {
  const excludedResources = [
    "theme",
    "environment",
    "dashboard",
    "category",
    "item",
  ];

  const resources = useResourceDefinitions();
  const allResourcecs = Object.keys(resources);
  const tenant = sessionStorage.getItem("current-tenant");
  const brand = sessionStorage.getItem("current-brand");
  const navigate = useNavigate();
  const location = useLocation();
  const appExtension: AppExtension =
    AppExtensionProviderRegistry.getDefaultProvider()?.getAppExtension();
  const defaultRoute = appExtension.getDefaultRoute();
  const [dataOpen, setDataOpen] = useState(false);
  const [aiOpen, setAiOpen] = useState(false);
  const [appsOpen, setAppsOpen] = useState(false);
  const [expandedApps, setExpandedApps] = useState<Set<string>>(new Set());
  
  // Load dynamic entities for the Data menu
  const { entities: dynamicEntities, loading: entitiesLoading } = useDynamicEntities();
  
  // Load Data Model hierarchy (domains and schemas)
  const { domains, loading: domainsLoading } = useDataModelHierarchy();
  const [expandedDomains, setExpandedDomains] = useState<Set<string>>(new Set());

  const handleDataClick = () => {
    // Navigate to the "Data" section and toggle the collapse state
    navigate("/data");
    setDataOpen(!dataOpen);
  };

  const handleAIClick = () => {
    // Navigate to the "AI" section and toggle the collapse state
    navigate("/ai");
    setAiOpen(!aiOpen);
  };

  const handleAppsClick = () => {
    // Navigate to the "Applications" section and toggle the collapse state
    navigate("/application");
    setAppsOpen(!appsOpen);
  };

  // Get icon for application type
  const getApplicationIcon = (appKey: string, isGenerated: boolean) => {
    if (appKey === 'framework') return FrameworkIcon;
    if (appKey === 'domain') return DomainIcon;
    if (isGenerated) return GeneratedIcon;
    return DataIcon;
  };

  // Toggle domain section expansion
  const toggleDomainSection = (domainId: string) => {
    const newExpanded = new Set(expandedDomains);
    if (newExpanded.has(domainId)) {
      newExpanded.delete(domainId);
    } else {
      newExpanded.add(domainId);
    }
    setExpandedDomains(newExpanded);
  };
  
  // Get icon for domain
  const getDomainIcon = (domainName: string) => {
    const name = domainName?.toLowerCase() || '';
    if (name.includes('identity') || name.includes('people')) return PeopleIcon;
    if (name.includes('commerce') || name.includes('sales')) return CommerceIcon;
    if (name.includes('operation')) return OperationsIcon;
    return DomainIcon;
  };

  const ResourceSubMenuItem = ({ resourceName, onClick, padding = 16 }) => {
    const resources = useResourceDefinitions();
    const resource = resources[resourceName];

    console.log('resource', resource.name)
    console.log('resourceIcon', resource.icon)

    if (!resource) {
      return null; // If the resource doesn't exist, don't render anything
    }

    return (
      <SubMenuItem
        to={`/${resourceName}`}
        label={resource?.options?.label || resourceName}
        Icon={resource?.icon} // Pass undefined if no icon (avoids React.Fragment warning)
        onClick={onClick}
        padding={padding}
      />
    );
  };

  const SubMenuItem = ({
    to,
    label,
    Icon,
    onClick,
    padding = 32,
    fontSize = "0.875rem",
  }) => {
    // Don't render Icon component if it's React.Fragment (causes warnings)
    const iconElement = Icon && Icon !== React.Fragment 
      ? <Icon style={{ fontSize: "18px", minWidth: "24px" }} /> 
      : null;
    
    return (
      <MenuItemLink
        to={to}
        primaryText={label}
        leftIcon={iconElement}
        onClick={onClick}
        style={{
          paddingLeft: `${padding}px`,
          fontSize: fontSize,
          whiteSpace: "nowrap",
          overflow: "visible",
          textOverflow: "ellipsis",
          minHeight: "36px",
          letterSpacing: "normal",
          wordSpacing: "normal",
        }}
        sx={{
          '& .MuiListItemIcon-root': {
            minWidth: '36px',
          },
          '& .MuiListItemText-primary': {
            fontSize: fontSize,
            letterSpacing: 'normal !important',
            wordSpacing: 'normal !important',
          }
        }}
        sidebarIsOpen={true}
      />
    );
  };

  if (!tenant && allResourcecs && allResourcecs.length) {
    return (
      <div>
        <MenuItemLink
          key="tenant"
          to={`/tenant`}
          primaryText={
            (resources["tenant"].options &&
              resources["tenant"].options.label) ||
            "tenant"
          }
          leftIcon={
            resources["tenant"].icon ? (
              React.createElement(resources["tenant"].icon)
            ) : (
              <CategoryIcon />
            )
          }
          onClick={onMenuClick}
        />
        {logout}
      </div>
    );
  }

  // Check if this is mmadmin environment
  const selectedEnv = sessionStorage.getItem('selectedEnv');
  const envLower = selectedEnv?.toLowerCase().replace(/[-_\s]/g, '') || ''; // Remove hyphens, underscores, spaces
  const isMMAdmin = envLower.includes('admin') || envLower.includes('madmobile');
  
  // Check if this is a location-level environment by checking sessionStorage
  // This gets set by the environment selector or can be detected from backend
  const isLocationEnv = sessionStorage.getItem('hasLocationContext') === 'true';

  return (
    <div>
      <MenuItemLink to="/" primaryText="Welcome" leftIcon={<DashboardIcon />} />

      {/* Conditional Tenant Menu Item */}
      {isMMAdmin && (
        <MenuItemLink
          to="/tenant"
          primaryText={
            (resources["tenant"]?.options &&
              resources["tenant"].options.label) ||
            "Tenants"
          }
          leftIcon={
            resources["tenant"]?.icon ? (
              React.createElement(resources["tenant"]?.icon)
            ) : (
              <CategoryIcon />
            )
          }
          onClick={onMenuClick}
        />
      )}
      
      {!isMMAdmin && !isLocationEnv && (
        <MenuItemLink
          to="/my-tenant"
          primaryText="Tenant Settings"
          leftIcon={
            resources["tenant"]?.icon ? (
              React.createElement(resources["tenant"]?.icon)
            ) : (
              <CategoryIcon />
            )
          }
          onClick={onMenuClick}
        />
      )}
      
      {isLocationEnv && (
        <MenuItemLink
          to="/my-location"
          primaryText="Location Settings"
          leftIcon={
            resources["location"]?.icon ? (
              React.createElement(resources["location"]?.icon)
            ) : (
              <CategoryIcon />
            )
          }
          onClick={onMenuClick}
        />
      )}

      <ListItem onClick={handleAppsClick}>
        <ListItemIcon style={{ minWidth: "40px" }}>
          <AppsIcon />
        </ListItemIcon>
        <ListItemText primary="Applications" style={{ textAlign: "left" }} />
        {appsOpen ? <ExpandLessIcon /> : <ExpandMoreIcon />}
      </ListItem>

      {/* Applications Nested Menu Items */}
      <Collapse
        in={appsOpen}
        timeout="auto"
        unmountOnExit
        style={{ paddingLeft: "10px" }}
      >
        <Paper
          elevation={2}
          style={{
            width: "95%",
            marginLeft: "10px",
            borderRadius: "8px",
            maxHeight: "250px",
            overflowY: "auto",
          }}
        >
          <List component="div" disablePadding>
            <SubMenuItem
              to="/applications/observatory"
              label="Observatory"
              Icon={VisibilityIcon}
              onClick={onMenuClick}
              padding={16}
            />
            <ResourceSubMenuItem
              resourceName="application"
              onClick={onMenuClick}
            />
          </List>
        </Paper>
      </Collapse>


      <ListItem onClick={handleAIClick}>
        <ListItemIcon style={{ minWidth: "40px" }}>
          <ConfigIcon />
        </ListItemIcon>
        <ListItemText primary="AI" style={{ textAlign: "left" }} />
        {aiOpen ? <ExpandLessIcon /> : <ExpandMoreIcon />}
      </ListItem>

      {/* Nested Menu Items */}
      <Collapse
        in={aiOpen}
        timeout="auto"
        unmountOnExit
        style={{ paddingLeft: "10px" }}
      >
        <Paper
          elevation={2}
          style={{
            width: "95%",
            marginLeft: "10px",
            borderRadius: "8px",
            maxHeight: "250px", // Reasonable max for AI section
            overflowY: "auto",
          }}
        >
          <List component="div" disablePadding>
            <SubMenuItem
              to="/ai/observatory"
              label="Observatory"
              Icon={VisibilityIcon}
              onClick={onMenuClick}
              padding={16}
            />
            <ResourceSubMenuItem
              resourceName="aiassistant"
              onClick={onMenuClick}
            />
            <ResourceSubMenuItem
              resourceName="aifileinstance"
              onClick={onMenuClick}
            />
             <ResourceSubMenuItem
              resourceName="aifilestore"
              onClick={onMenuClick}
            />
          </List>
        </Paper>
      </Collapse>

      <MenuItemLink
        to="/analytics-dashboards"
        primaryText="Dashboards"
        leftIcon={<AnalyticsIcon />}
        onClick={onMenuClick}
      />

      {/* Analytics menu item hidden */}
      {/* <MenuItemLink
        to="/analytics"
        primaryText="Analytics"
        leftIcon={<AnalyticsIcon />}
        onClick={onMenuClick}
      /> */}

      <MenuItemLink
        to="/reports"
        primaryText="Reports"
        leftIcon={<AssessmentIcon />}
        onClick={onMenuClick}
      />

      <MenuItemLink
        to="/developer"
        primaryText="Developer"
        leftIcon={<DeveloperIcon />}
        onClick={onMenuClick}
      />
      <MenuItemLink
        to="/compose"
        primaryText="Integration"
        leftIcon={<ComposeIcon />}
        onClick={onMenuClick}
      />

      <ListItem onClick={handleDataClick}>
        <ListItemIcon style={{ minWidth: "40px" }}>
          <ConfigIcon />
        </ListItemIcon>
        <ListItemText primary="Data" style={{ textAlign: "left" }} />
        {dataOpen ? <ExpandLessIcon /> : <ExpandMoreIcon />}
      </ListItem>

      {/* Nested Menu Items */}
      <Collapse
        in={dataOpen}
        timeout="auto"
        unmountOnExit
        style={{ paddingLeft: "10px" }}
      >
        <Paper
          elevation={2}
          style={{
            width: "95%",
            marginLeft: "10px",
            borderRadius: "8px",
            maxHeight: "calc(100vh - 200px)", // Dynamic height based on viewport
            overflowY: "auto",
          }}
        >
          <List component="div" disablePadding>
            {/* Data Model management - primary data administration */}
            <SubMenuItem
              to="datamodel"
              label="Data Models"
              Icon={FrameworkIcon}
              onClick={onMenuClick}
              fontSize="0.9rem"
            />
            
            {/* Domain-grouped entities from Data Models */}
            {!domainsLoading && domains.length > 0 && domains.map(domain => {
              const DomainIconComponent = getDomainIcon(domain.name);
              
              console.log(`🎨 Rendering domain section:`, {
                id: domain.id,
                name: domain.name,
                schemaCount: domain.schemas?.length || 0,
                schemaNames: domain.schemas?.map(s => s.name || s.key)
              });
              
              return (
                <div key={`domain-${domain.id}-${domain.schemas?.length || 0}`}>
                  {/* Domain group header - clickable to expand/collapse */}
                  <ListItem 
                    onClick={() => toggleDomainSection(domain.id)}
                    sx={{ 
                      paddingLeft: '32px',
                      paddingY: '4px',
                      cursor: 'pointer',
                      borderLeft: expandedDomains.has(domain.id) ? '2px solid #2196f3' : '2px solid transparent',
                      backgroundColor: expandedDomains.has(domain.id) ? 'rgba(33, 150, 243, 0.08)' : 'transparent',
                      '&:hover': { 
                        backgroundColor: expandedDomains.has(domain.id) 
                          ? 'rgba(33, 150, 243, 0.12)' 
                          : 'rgba(255, 255, 255, 0.08)' 
                      }
                    }}
                  >
                    <ListItemIcon sx={{ minWidth: '32px' }}>
                      <DomainIconComponent sx={{ fontSize: '18px' }} />
                    </ListItemIcon>
                    <ListItemText 
                      primary={domain.name}
                      secondary={`${domain.schemas.length} entities`}
                      primaryTypographyProps={{ 
                        fontSize: '0.85rem',
                        fontWeight: 'medium',
                        color: expandedDomains.has(domain.id) ? '#2196f3' : 'inherit'
                      }}
                      secondaryTypographyProps={{
                        fontSize: '0.7rem',
                        color: 'rgba(255, 255, 255, 0.6)'
                      }}
                    />
                    {expandedDomains.has(domain.id) ? <ExpandLessIcon sx={{ fontSize: '16px' }} /> : <ExpandMoreIcon sx={{ fontSize: '16px' }} />}
                  </ListItem>
                  
                  {/* Collapsible schemas within this domain */}
                  <Collapse in={expandedDomains.has(domain.id)} timeout="auto" unmountOnExit>
                    <List component="div" disablePadding>
                      {domain.schemas
                        .sort((a, b) => (a.name || a.key || '').localeCompare(b.name || b.key || ''))
                        .map((schema, index) => {
                          // Debug logging for schema rendering
                          const label = schema.name || schema.key || 'Unnamed';
                          // Create a unique key even if schema.key is empty
                          const schemaKey = schema.key || schema.name || `schema_${index}`;
                          const uniqueKey = `${domain.id}-${schemaKey}-${index}`;
                          
                          if (!schema.name) {
                            console.warn(`⚠️ Schema missing name in domain ${domain.name}:`, schema);
                          }
                          
                          return (
                            <SubMenuItem
                              key={uniqueKey}
                              to={schema.key || schema.name || '#'}
                              label={label}
                              Icon={SchemaIcon}
                              onClick={onMenuClick}
                              padding={52}
                              fontSize="0.75rem"
                            />
                          );
                        })}
                    </List>
                  </Collapse>
                </div>
              );
            })}
            
            {domainsLoading && (
              <SubMenuItem
                to="#"
                label="Loading domains..."
                Icon={DataIcon}
                onClick={() => {}}
              />
            )}
            
            {/* Standalone Generated Entities (not part of domains) */}
            {!entitiesLoading && dynamicEntities.length > 0 && (() => {
              // Get all entity names that are already part of domains
              const domainEntityNames = new Set();
              domains.forEach(domain => {
                domain.schemas.forEach(schema => {
                  if (schema?.name) {
                    domainEntityNames.add(schema.name.toLowerCase());
                  }
                });
              });

              // Filter for entities that are not in domains and are generated
              const standaloneEntities = dynamicEntities.filter(entity => {
                const isPlatformEntity = entity.metadata.applicationContext === 'framework' || 
                                         entity.metadata.applicationContext === 'unknown' ||
                                         entity.metadata.applicationContext === 'analytics';
                
                const isInDomain = domainEntityNames.has(entity?.name?.toLowerCase() || '');
                
                // Include only non-platform entities that are not in domains
                return !isPlatformEntity && !isInDomain;
              });
              
              // Only show the section if there are standalone entities
              if (standaloneEntities.length === 0) return null;
              
              return (
                <>
                  <SubMenuItem
                    to="#"
                    label="Generated Entities"
                    Icon={GeneratedIcon}
                    onClick={() => {}}
                    fontSize="0.8rem"
                    padding={32}
                  />
                  {standaloneEntities.map(entity => (
                    <SubMenuItem
                      key={entity.resourceName}
                      to={entity.resourceName}
                      label={entity.label}
                      Icon={GeneratedIcon}
                      onClick={onMenuClick}
                      padding={52}
                      fontSize="0.75rem"
                    />
                  ))}
                </>
              );
            })()}
          </List>
        </Paper>
      </Collapse>

      <MenuItemLink
        to="/connection"
        primaryText="Connections"
        leftIcon={<CableIcon />}
        onClick={onMenuClick}
      />

      <MenuItemLink
        to="/authentication-provider"
        primaryText="Authentication"
        leftIcon={<SecurityIcon />}
        onClick={onMenuClick}
      />

      {logout}
    </div>
  );
};

export default Menu;

/*
       {Object.keys(resources).filter(name => !excludedResources.includes(name)).map(name => (
                  <MenuItemLink
                      key={name}
                      to={`/${name}`}
                      primaryText={(resources[name].options && resources[name].options.label) || name}
                      leftIcon={resources[name].icon ? React.createElement(resources[name].icon) : <CategoryIcon />}
                      onClick={onMenuClick}
                  />
              ))}


               {/** 
      <MenuItemLink
        key={"tenant"}
        to={`/tenant`}
        primaryText={
          (resources["tenant"]?.options &&
            resources["tenant"]?.options?.label) ||
          "tenant"
        }
        leftIcon={
          resources["tenant"]?.icon ? (
            React.createElement(resources["tenant"]?.icon)
          ) : (
            <CategoryIcon />
          )
        }
        onClick={onMenuClick}
      />
      <MenuItemLink
        key={"brand"}
        to={`/brand`}
        primaryText={
          (resources["brand"]?.options && resources["brand"]?.options?.label) ||
          "brand"
        }
        leftIcon={
          resources["brand"]?.icon ? (
            React.createElement(resources["brand"]?.icon)
          ) : (
            <CategoryIcon />
          )
        }
        onClick={onMenuClick}
      />
                  */
